import { Typography } from '@mui/material'
import { intl } from '../../../../../../i18n'
import { SearchItemLabelHorizontalContainer } from '../../../../../components/search-forms/search-item-label-container'
import { BlankableDateTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/BlankableDateTermConditionItemLabel'
import { DateTimeTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/DateTimeTermConditionItemLabel'
import { TextMatchConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/TextMatchConditionItemLabel'
import { ProjectMemberAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/ProjectMemberAutocompleteConditionItemLabel'
import { SprintAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/SprintAutocompleteConditionItemLabel'
import { SprintMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/SprintMultiAutocompleteConditionItemLabel'
import { TeamAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TeamAutocompleteConditionItemLabel'
import { TicketTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TicketTypeCheckboxSelectConditionItemLabel'
import { WbsItemPriorityCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemPriorityCheckboxSelectConditionItemLabel'
import { WbsItemStatusCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemStatusCheckboxSelectConditionItemLabel'
import { WbsItemTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemTypeCheckboxSelectConditionItemLabel'
import { WbsItemSearchSearchConditionV2 } from '../../../hooks/useSearchConditionV2'
import { useOnSingleValueChangedGenerator } from './useOnSingleValueChangedGenerator'
import { useCallback, useMemo } from 'react'
import { TextMatchCondition } from '../../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { TagMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TagMultiAutocompleteConditionItemLabel'
import { featureFlagService } from '../../../../../../utils/feature-flag'
import { colorPalette } from '../../../../../style/colorPalette'
import { TextConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/TextConditionItemLabel'

type WbsItemSearchSearchItemLabelsV2Props = {
  searchCondition: WbsItemSearchSearchConditionV2
  isLoading: boolean
  onChange: (updated: Partial<WbsItemSearchSearchConditionV2>) => void
  search: () => void
  onClose: () => void
}

export const WbsItemSearchSearchItemLabelsV2 = ({
  searchCondition,
  isLoading,
  onChange,
  search,
  onClose,
}: WbsItemSearchSearchItemLabelsV2Props) => {
  const { onChangeValueGenerator, onChangeBlankGenerator } =
    useOnSingleValueChangedGenerator(searchCondition, onChange)

  const onTagChange = useCallback(
    (newValue: string[] | undefined) => {
      if (!newValue || newValue.length === 0) {
        onChange({ tagUuids: undefined })
        return
      }
      const conditions: TextMatchCondition[] = newValue.map(uuid => ({
        value: uuid,
        mode: 'include' as const,
      }))
      onChange({ tagUuids: conditions })
    },
    [onChange]
  )

  const useSprintsMultiSelect = featureFlagService.isEnabled(
    'sprints-multi-select'
  )

  const noSearchCondition = useMemo(() => {
    return Object.values(searchCondition)
      .map(v => {
        if (typeof v === 'undefined') {
          return true
        }
        if (Array.isArray(v)) {
          return v.length === 0
        }
        return !v
      })
      .every(v => v)
  }, [searchCondition])

  return (
    <SearchItemLabelHorizontalContainer>
      {noSearchCondition ? (
        <Typography
          sx={{
            fontSize: '14px',
            color: colorPalette.monotone[5],
            fontWeight: 500,
            marginLeft: '10px',
          }}
        >
          {intl.formatMessage({ id: 'wbs.search.no.search.condition' })}
        </Typography>
      ) : (
        <>
          <TextMatchConditionItemLabel
            header={intl.formatMessage({ id: 'wbsItemSearchFilterPanel.code' })}
            value={searchCondition.code}
            onChange={v => onChange({ code: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemTypeCheckboxSelectConditionItemLabel
            value={searchCondition.types}
            onChange={onChangeValueGenerator('types')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TicketTypeCheckboxSelectConditionItemLabel
            value={searchCondition.ticketTypes}
            onChange={onChangeValueGenerator('ticketTypes')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemStatusCheckboxSelectConditionItemLabel
            value={searchCondition.status}
            onChange={onChangeValueGenerator('status')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemPriorityCheckboxSelectConditionItemLabel
            value={searchCondition.priority}
            onChange={onChangeValueGenerator('priority')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextMatchConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.displayName',
            })}
            value={searchCondition.displayName}
            onChange={v => onChange({ displayName: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.description',
            })}
            value={searchCondition.description}
            onChange={onChangeValueGenerator('description')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TeamAutocompleteConditionItemLabel
            value={searchCondition.teamUuid}
            onChange={onChangeValueGenerator('teamUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          {useSprintsMultiSelect ? (
            <SprintMultiAutocompleteConditionItemLabel
              value={searchCondition.sprintUuids}
              teamUuid={searchCondition.teamUuid}
              onChange={onChangeValueGenerator('sprintUuids')}
              onClose={onClose}
              search={search}
              isLoading={isLoading}
            />
          ) : (
            <SprintAutocompleteConditionItemLabel
              value={searchCondition.sprintUuid}
              teamUuid={searchCondition.teamUuid}
              onChange={onChangeValueGenerator('sprintUuid')}
              onClose={onClose}
              search={search}
              isLoading={isLoading}
            />
          )}
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.accountable',
            })}
            value={searchCondition.accountableUuid}
            onChange={onChangeValueGenerator('accountableUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.responsible',
            })}
            value={searchCondition.responsibleUuid}
            onChange={onChangeValueGenerator('responsibleUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.assignee',
            })}
            value={searchCondition.assigneeUuid}
            onChange={onChangeValueGenerator('assigneeUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TagMultiAutocompleteConditionItemLabel
            value={searchCondition.tagUuids?.map(c => c.value)}
            onChange={onTagChange}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledStartDate',
            })}
            value={searchCondition.scheduledStartDate}
            onChange={onChangeValueGenerator('scheduledStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledStartDate}
            onChangeBlank={onChangeBlankGenerator('scheduledStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledEndDate',
            })}
            value={searchCondition.scheduledEndDate}
            onChange={onChangeValueGenerator('scheduledEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledEndDate}
            onChangeBlank={onChangeBlankGenerator('scheduledEndDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualStartDate',
            })}
            value={searchCondition.actualStartDate}
            onChange={onChangeValueGenerator('actualStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualStartDate}
            onChangeBlank={onChangeBlankGenerator('actualStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualEndDate',
            })}
            value={searchCondition.actualEndDate}
            onChange={onChangeValueGenerator('actualEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualEndDate}
            onChangeBlank={onChangeBlankGenerator('actualEndDate')}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdAt',
            })}
            value={searchCondition.createdAt}
            onChange={onChangeValueGenerator('createdAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedAt',
            })}
            value={searchCondition.updatedAt}
            onChange={onChangeValueGenerator('updatedAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdBy',
            })}
            value={searchCondition.createdByUuid}
            onChange={onChangeValueGenerator('createdByUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedBy',
            })}
            value={searchCondition.updatedByUuid}
            onChange={onChangeValueGenerator('updatedByUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
        </>
      )}
    </SearchItemLabelHorizontalContainer>
  )
}
